import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "@/hooks/use-business";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";

export type ServiceCategory = Tables<"service_categories">;
type ServiceCategoryInsert = TablesInsert<"service_categories">;
type ServiceCategoryUpdate = TablesUpdate<"service_categories">;

export function useServiceCategories() {
  const { business } = useBusiness();
  const { toast } = useToast();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    if (!business) {
      setCategories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("service_categories")
      .select("*")
      .eq("business_id", business.id)
      .order("position", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      setLoading(false);
      return;
    }

    setCategories((data || []) as ServiceCategory[]);
    setLoading(false);
  }, [business]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = async (input: Omit<ServiceCategoryInsert, "business_id">) => {
    if (!business) throw new Error("No business found");
    const { data, error } = await supabase
      .from("service_categories")
      .insert({ ...input, business_id: business.id })
      .select()
      .single();
    if (error) throw error;
    toast({ title: "Category created", description: `${(data as any)?.name || "Category"} added.` });
    await fetchCategories();
    return data as ServiceCategory;
  };

  const updateCategory = async (id: string, patch: ServiceCategoryUpdate) => {
    const { data, error } = await supabase
      .from("service_categories")
      .update(patch)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    toast({ title: "Category updated" });
    await fetchCategories();
    return data as ServiceCategory;
  };

  const deleteCategory = async (id: string) => {
    const { error } = await supabase.from("service_categories").delete().eq("id", id);
    if (error) throw error;
    toast({ title: "Category deleted" });
    await fetchCategories();
  };

  return { categories, loading, refetch: fetchCategories, createCategory, updateCategory, deleteCategory };
}

